import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';

import { CustomAlert, CustomButton, CustomInput, CustomSelect, Picker } from '../../../../../shared/uiComponents';
import { SelectChangeEvent } from '@mui/material';
import { dateNormalizer, errorNormalizer, validateDecimalNumber } from '../../../../../shared/Helpers/functions';
import { useCompareDates } from '../../../../../shared/Helpers/hooks';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { getClientFunders } from '../../../../../redux/State/clientSlice/fundingSlice';
import { AuthorizationProperties } from '../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import {
	getInsuranceAuthorizationSessionTypes,
	getInsuranceAuthorizations,
	saveAssessmentInsuranceAuthorizations,
} from '../../../../../redux/State/clientSlice/insuranceAuthorizationSlice';

const AssessmentContent = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const [params] = useSearchParams();

	const today = new Date();
	const todayDayjs = dayjs(today);
	const futureDateMonth = dayjs(today).add(1, 'month');

	const [startDateValue, setStartDateValue] = useState<Dayjs>(todayDayjs);
	const [endDateValue, setEndDateValue] = useState<Dayjs>(futureDateMonth);
	const [totalHours, setTotalHours] = useState<string>('0');
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedFunderId, setSelectedFunderId] = useState<string>('insurance');
	const clientFunders = useSelector((state) => state.funding.clientFunders);

	const sessionTypes = useSelector((state) => state.authorization.insuranceAuthorizationSessionTypes);
	const { hasError, message } = useCompareDates(startDateValue, endDateValue);

	const onChangeAssessmentStartDate = (value: Dayjs | null) => {
		if (!value) return;
		setStartDateValue(value);
	};

	const onChangeAssessmentEndDate = (value: Dayjs | null) => {
		if (!value) return;
		setEndDateValue(value);
	};

	useEffect(() => {
		if (!clientId) return;
		dispatch(getClientFunders(clientId));
	}, [dispatch, clientId]);

	// Only offer funders that have at least one authorization session type configured.
	// Funders without auth catalog entries (e.g. ELC, RequiresAuthorization=false) are
	// excluded so the dropdown never leads to a dead-end with no session types to pick.
	const fundersWithAuthTypes = clientFunders.filter((f) =>
		sessionTypes.some((st) => st.funderId === f.funderId),
	);

	useEffect(() => {
		if (fundersWithAuthTypes.length === 1) {
			setSelectedFunderId(fundersWithAuthTypes[0].funderId);
		}
	}, [fundersWithAuthTypes]);

	const funderOptions = [
		{ id: 'insurance', name: 'Insurance' },
		...fundersWithAuthTypes.map((f) => ({ id: f.funderId, name: f.funderName })),
	];

	const onFunderChange = (event: SelectChangeEvent<string>) => {
		setSelectedFunderId(event.target.value);
	};

	// The single Assessment code that belongs to the currently selected funding source:
	// insurance (funderId undefined/null) when "Insurance (default)" is selected, or the
	// matching funder's DOE code when a funder is selected. Never falls back to "whichever
	// row happens to be first" — that silently mismatched the code against the chosen funder.
	const selectedSessionType = sessionTypes.find((x) =>
		selectedFunderId === 'insurance' ? !x.funderId : x.funderId === selectedFunderId,
	);

	const fetchAuthorizations = () => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		if (!clientId) return;
		dispatch(getInsuranceAuthorizations({ clientId, page, pageSize }));
	};

	const saveAuthorizationAssessment = () => {
		if (!startDateValue || !endDateValue || !clientId || !selectedSessionType) return;

		const startDate = dateNormalizer(startDateValue);
		const endDate = dateNormalizer(endDateValue);

		const data: AuthorizationProperties = {
			startDate,
			endDate,
			funderId: selectedFunderId === 'insurance' ? null : selectedFunderId,
			authorizationDetails: [
				{
					authorizationSessionTypeId: selectedSessionType.id,
					cptCode: selectedSessionType.cptCode,
					totalHours: parseFloat(totalHours),
					frequency: 2,
					hoursPerWeek: null,
					sessionTypeId: selectedSessionType.sessionTypeId,
				},
			],
		};
		setLoading(true);
		dispatch(saveAssessmentInsuranceAuthorizations({ clientId, data }))
			.then(unwrapResult)
			.then(() => {
				onClose();
				toast('Success');
				fetchAuthorizations();
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	const onTotalHoursChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setTotalHours(value);
	};

	return (
		<>
			{fundersWithAuthTypes.length > 0 && (
				<CustomSelect
					label="Funding Source"
					data={funderOptions}
					value={selectedFunderId}
					setValue={onFunderChange}
					className="marginBottom16"
				/>
			)}
			<Picker.CustomDate
				label="Start Date:"
				value={startDateValue}
				onChange={onChangeAssessmentStartDate}
				className="marginBottom16 marginTop16"
			/>
			<Picker.CustomDate
				label="End Date:"
				value={endDateValue}
				onChange={onChangeAssessmentEndDate}
				className="marginBottom16"
				errorMessage={message}
				error={hasError}
			/>
			<div className="insuranceAuthorizationInputsRow marginBottom16">
				<CustomSelect
					label="Session type"
					data={selectedSessionType ? [selectedSessionType] : []}
					value={selectedSessionType?.id ?? ''}
					setValue={() => null}
					disabled
				/>
				<CustomInput label="Total hours" value={totalHours} onChange={onTotalHoursChange} />
			</div>
			<CustomButton
				title="Save Assessment"
				onClick={saveAuthorizationAssessment}
				disabled={hasError || !totalHours || !selectedSessionType}
				loading={loading}
			/>
		</>
	);
};

const AddAssessmentInsuranceAuthorization = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [open, setOpen] = useState<boolean>(false);

	const openHandler = () => {
		dispatch(getInsuranceAuthorizationSessionTypes('1'));
		setOpen(true);
	};

	const closeHandler = () => setOpen(false);

	return (
		<>
			<CustomButton title="Add Assessment" onClick={openHandler} />
			<CustomAlert
				title="Assessment"
				open={open}
				onClose={closeHandler}
				contentHeight="550px"
				Content={({ onClose }) => <AssessmentContent onClose={onClose} />}
			/>
		</>
	);
};

export default AddAssessmentInsuranceAuthorization;
